import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { ToastrService } from 'ngx-toastr';

import { AppIconComponent } from '../../../icon/icon.component';
import { CALL_STATE, CALL_TYPE } from '../../constants';
import { CallStoreService } from '../../services/call-store.service';
import { CallZegoService } from '../../services/call-zego.service';

@Component({
  selector: 'app-call-action-bar',
  standalone: true,
  imports: [AppIconComponent, NzButtonModule],
  templateUrl: './call-action-bar.component.html',
  styleUrl: './call-action-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CallActionBarComponent implements AfterViewInit {
  showCallMeta = input<boolean>(false);

  private readonly callStore = inject(CallStoreService);
  private readonly callZegoService = inject(CallZegoService);
  private readonly toastr = inject(ToastrService);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('actionBarContainer')
  actionBarContainer!: ElementRef<HTMLDivElement>;

  readonly visibleExtraButtons = signal(3);
  readonly showMenu = signal(false);

  CALL_STATE = CALL_STATE;
  CALL_TYPE = CALL_TYPE;

  private resizeObserver?: ResizeObserver;
  private resizeFrame?: number;

  readonly callState = this.callStore.callState;
  readonly currentCall = this.callStore.currentCall;
  readonly callType = this.callStore.callType;
  readonly hasCurrentCall = this.callStore.hasCurrentCall;
  readonly isInviter = this.callStore.isInviter;

  readonly localMediaPermissionService = this.callStore.localMediaPermissionService;
  readonly isMicPermissionGranted = this.localMediaPermissionService.isPermissionMic;
  readonly isCameraPermissionGranted = this.localMediaPermissionService.isPermissionCam;
  readonly hasMicDevice = this.localMediaPermissionService.hasMicDevice;
  readonly hasCamDevice = this.localMediaPermissionService.hasCamDevice;
  readonly localMicState = this.callStore.localMicState;
  readonly localCamState = this.callStore.localCamState;
  readonly localStream = this.callStore.localStream;
  readonly localStreamId = this.callStore.localStreamId;
  readonly isScreenSharing = this.callStore.isScreenSharing;
  readonly callDuration = this.callStore.callDurationText;

  readonly isDisabledActionBar = computed(() => this.callState() !== CALL_STATE.CONNECTED);
  // Nếu không có user trong phòng thì không cho share màn hình
  isDisableShareScreen = computed(() => !this.callZegoService.hasUserInRoom());

  readonly closeModalEvt = output<void>();
  readonly toggleCamEvt = output<void>();
  readonly toggleShareScreenEvt = output<void>();

  ngAfterViewInit(): void {
    this.initResizeObserver();

    this.destroyRef.onDestroy(() => {
      this.resizeObserver?.disconnect();

      if (this.resizeFrame) {
        cancelAnimationFrame(this.resizeFrame);
      }
    });
  }

  private initResizeObserver(): void {
    this.resizeObserver = new ResizeObserver(([entry]) => {
      if (!entry) return;

      if (this.resizeFrame) {
        cancelAnimationFrame(this.resizeFrame);
      }

      this.resizeFrame = requestAnimationFrame(() => {
        const width = entry.contentRect.width;

        const nextVisible = width < 312 ? 0 : width < 376 ? 1 : width < 440 ? 2 : 3;

        if (this.visibleExtraButtons() !== nextVisible) {
          this.visibleExtraButtons.set(nextVisible);
        }
      });
    });

    this.resizeObserver.observe(this.actionBarContainer.nativeElement);
  }

  handleClose(): void {
    this.closeModalEvt.emit();
  }

  async reCall(): Promise<void> {
    const invitees = this.callStore
      .participants()
      .filter((p) => p.userId !== this.callStore.zimService.userId)
      .map((p) => p.userId);

    const type = this.callType();
    const extendedData = JSON.stringify(this.currentCall()?.extendedData);

    if (invitees.length > 0) {
      await this.callStore.callInvite(invitees, type, extendedData);
    }
  }

  async handleEndCall(): Promise<void> {
    await this.callStore.endCurrentCall();
  }

  async requestCamMicPermission(type: 'cam' | 'mic'): Promise<void> {
    const granted = await this.localMediaPermissionService.requestCameraMicPermission(type);

    if (!granted) return;
    if (type === 'cam') {
      this.toggleCamEvt.emit();
      return;
    }
    this.handleToggleMic();
  }

  handleToggleMic(): void {
    this.callStore.toggleMic();
  }

  handleToggleCam(): void {
    this.toggleCamEvt.emit();
  }

  handleToggleScreen(): void {
    this.toggleShareScreenEvt.emit();
  }

  toggleUpdate(): void {
    this.toastr.info('Tính năng đang phát triển!');
  }
}
